import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Drawer, Input, Popconfirm, Row, Space, Table, Tooltip, Typography, DatePicker, Switch } from 'antd';
import type { TableProps } from 'antd';
import type { FilterValue, SorterResult, TablePaginationConfig } from 'antd/es/table/interface';
import type { SortOrder } from 'antd/es/table/interface';
import { SearchOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { debounce } from 'lodash';
import TrashIcon from 'assets/icons/trash.svg';
import PenIcon from 'assets/icons/pen.svg';

import { openNotification } from '../../../utils';
import {
  listPublicHolidays,
  deletePublicHoliday,
  updatePublicHoliday,
} from '../services/public-holidayService';
import PublicHolidayForm from './Form';
import { PublicHoliday } from '../types/PublicHoliday';

const { RangePicker } = DatePicker;

type RangeValue = [Dayjs | null, Dayjs | null];

export default function PublicHolidayList(){
  const [data, setData] = useState<PublicHoliday[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | undefined>();

  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedDateRange, setSelectedDateRange] = useState<RangeValue | undefined>();
  const [pendingDateRange, setPendingDateRange] = useState<RangeValue | undefined>();

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const DEFAULT_SORT_COLUMN = 'startDate';
  const DEFAULT_SORT_ORDER: SortOrder = 'descend';
  const [sortState, setSortState] = useState<{ columnKey?: string; order?: SortOrder }>({
    columnKey: DEFAULT_SORT_COLUMN,
    order: DEFAULT_SORT_ORDER
  });
  const resolveSortField = (columnKey?: string) => {
    switch (columnKey) {
      case 'name':
        return 'name';
      case 'startDate':
        return 'startDate';
      case 'endDate':
        return 'endDate';
      default:
        return undefined;
    }
  };
  const resolveSortParam = () => {
    const fallback = `${DEFAULT_SORT_COLUMN},desc`;
    if (!sortState.columnKey || !sortState.order) return fallback;
    const field = resolveSortField(sortState.columnKey);
    if (!field) return fallback;
    const direction = sortState.order === 'descend' ? 'desc' : 'asc';
    return `${field},${direction}`;
  };

  const debouncedApplySearch = useMemo(() => debounce((val: string) => { setSearch(val); }, 300), []);
  useEffect(() => () => { debouncedApplySearch.cancel(); }, [debouncedApplySearch]);

  const fetchData = async () => {
    const filters: string[] = [];
    if (selectedDateRange?.[0]) {
      filters.push('startDate.greaterThanOrEqual=' + selectedDateRange[0].format('YYYY-MM-DD'));
    }
    if (selectedDateRange?.[1]) {
      filters.push('endDate.lessThanOrEqual=' + selectedDateRange[1].format('YYYY-MM-DD'));
    }
    if (search && search.length >= 2) {
      filters.push('name.contains=' + encodeURIComponent(search));
    }
    const query = filters.join('&');
    const sortParam = resolveSortParam();
    const res = await listPublicHolidays(page - 1, pageSize, sortParam, query);
    setData(res.content);
    setTotal(res.totalElements);
  };

  useEffect(() => { fetchData(); }, [page, pageSize, selectedDateRange, search, sortState.columnKey, sortState.order]);

  const openCreate = () => { setEditingId(undefined); setOpen(true); };
  const openEdit = (id:number) => { setEditingId(id); setOpen(true); };
  const handleClose = () => setOpen(false);

  const openFilter = () => { setPendingDateRange(selectedDateRange); setFilterOpen(true); };
  const closeFilter = () => setFilterOpen(false);
  const clearFilter = () => {
    setPendingDateRange(undefined);
    setSelectedDateRange(undefined);
    setPage(1);
  };

  const handleDelete = async (id:number) => {
    try {
      await deletePublicHoliday(id);
      openNotification('success', 'Başarılı', 'Resmi Tatil Silindi');
      fetchData();
    } catch (e) {
      openNotification('error', 'Hata', 'Resmi Tatil silme işlemi başarısız');
    }
  };

  const handleToggleHalfDay = async (record: PublicHoliday, checked: boolean) => {
    if (!record.id) {
      return;
    }
    try {
      await updatePublicHoliday(record.id, {
        name: record.name,
        startDate: record.startDate,
        endDate: record.endDate,
        halfDay: checked,
      });
      setData((prev) => prev.map((item) => (item.id === record.id ? { ...item, halfDay: checked } : item)));
      openNotification('success', 'Başarılı', `Resmi tatil ${checked ? 'Yarım Gün' : 'Tam Gün'} olarak güncellendi`);
    } catch (e) {
      openNotification('error', 'Hata', 'Resmi tatil güncelleme işlemi başarısız');
    }
  };

  const resolveSortOrder = (key: string) => (sortState.columnKey === key ? sortState.order : undefined);
  const columns = [
    { title: 'Resmi Tatil Adı', dataIndex: 'name', key: 'name', sorter: true, sortOrder: resolveSortOrder('name') },
    {
      title: 'Başlangıç Tarihi',
      dataIndex: 'startDate',
      key: 'startDate',
      sorter: true,
      sortOrder: resolveSortOrder('startDate'),
      render: (value: string) => (value ? dayjs(value).format('DD/MM/YYYY') : ''),
    },
    {
      title: 'Bitiş Tarihi',
      dataIndex: 'endDate',
      key: 'endDate',
      sorter: true,
      sortOrder: resolveSortOrder('endDate'),
      render: (value: string) => (value ? dayjs(value).format('DD/MM/YYYY') : ''),
    },
    {
      title: 'Gün',
      dataIndex: 'endDate',
      render: (_: string, record: PublicHoliday) => {
        if (!record.startDate || !record.endDate) {
          return '-';
        }
        const diff = dayjs(record.endDate).diff(dayjs(record.startDate), 'day') + 1;
        return record.halfDay ? '0.5' : diff;
      },
    },
    {
      title: 'Tam Gün / Yarım Gün',
      dataIndex: 'halfDay',
      render: (_: boolean, record: PublicHoliday) => (
        <Switch
          checked={record.halfDay ?? false}
          onChange={(checked) => handleToggleHalfDay(record, checked)}
          checkedChildren='Yarım Gün'
          unCheckedChildren='Tam Gün'
        />
      ),
    },
    {
      title: 'İşlemler',
      render: (_:any, r:any) => (
        <Space>
          <Tooltip title='Düzenle'>
            <Button
              icon={<img src={PenIcon} alt="pen" className="icon" />}
              shape="circle"
              onClick={() => openEdit(r.id)}
              className="icon-button"
            />
          </Tooltip>
          <Popconfirm title='Sil?' onConfirm={() => handleDelete(r.id)}>
            <Tooltip title='Sil'>
              <Button
                icon={<img src={TrashIcon} alt="trash" className="icon" />}
                shape="circle"
                className="icon-button ml-12px"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleTableChange: TableProps<PublicHoliday>['onChange'] = (
    _pagination: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter: SorterResult<PublicHoliday> | SorterResult<PublicHoliday>[]
  ) => {
    const normalizedSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    const nextOrder = (normalizedSorter?.order as SortOrder | undefined) ?? undefined;
    const rawKey = (normalizedSorter?.columnKey ?? normalizedSorter?.field) as string | undefined;
    const nextKey = nextOrder ? rawKey : undefined;
    if ((sortState.columnKey ?? undefined) === (nextKey ?? undefined) && sortState.order === nextOrder) {
      return;
    }
    setSortState({ columnKey: nextKey, order: nextOrder });
    setPage(1);
  };

  return (
    <>
      <Card
        className='br-30px kontrolx-card'
        title={(
          <Row align='bottom'>
            <Typography.Title level={4} style={{ margin: 0 }}>Resmi Tatiller</Typography.Title>
            <Typography.Text type='secondary' className='ms-5' style={{ fontWeight: 500 }}>
              <strong>({total})</strong> kayıt listelendi
            </Typography.Text>
          </Row>
        )}
        extra={(
          <Space>
            <Input
              allowClear
              placeholder="Ara"
              prefix={<SearchOutlined style={{ color: '#b8b9bc' }} />}
              className="d-input d-input-form d-input-filter"
              style={{ width: 200 }}
              value={searchInput}
              onChange={(e) => {
                const val = e.target.value || '';
                setSearchInput(val);
                if (val.length >= 2) {
                  debouncedApplySearch(val);
                } else if (val.length === 0) {
                  debouncedApplySearch('');
                }
              }}
            />
            <Button type='primary' onClick={openFilter}>Filtrele</Button>
            <Button type='primary' onClick={openCreate}>Ekle</Button>
          </Space>
        )}
      >
        <Table
          rowKey='id'
          dataSource={data}
          columns={columns}
          onChange={handleTableChange}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: ['50', '100', '250'],
            onChange: (p, s) => {
              setPage(p);
              setPageSize(s);
            },
          }}
        />
      </Card>
      <Drawer open={open} onClose={handleClose} destroyOnClose width={480} title={editingId ? 'Resmi Tatil Düzenle' : 'Resmi Tatil Ekle'}>
        <PublicHolidayForm id={editingId} onSuccess={() => { handleClose(); fetchData(); }} />
      </Drawer>
      <Drawer open={filterOpen} onClose={closeFilter} destroyOnClose width={420} title='Filtre'>
        <Space direction='vertical' style={{ width: '100%' }} size='large'>
          <div>
            <Typography.Text>Tarih Aralığı</Typography.Text>
            <RangePicker
              style={{ width: '100%', marginTop: 8 }}
              value={pendingDateRange}
              onChange={(val) => setPendingDateRange(val ? (val as RangeValue) : undefined)}
              format='DD/MM/YYYY'
            />
          </div>
          <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
            <Button onClick={clearFilter}>Temizle</Button>
            <Button
              type='primary'
              onClick={() => {
                setSelectedDateRange(pendingDateRange ? [...pendingDateRange] as RangeValue : undefined);
                closeFilter();
              }}
            >
              Filtrele
            </Button>
          </Space>
        </Space>
      </Drawer>
    </>
  );
}
